/* THE PANEL. Every panel in the app is this one: a thin header across the top
   holding its name and its controls, and a body below it that scrolls on its
   own. Styled once, as `.panel` in app.css — a screen that wants a panel calls
   `panel()` rather than building its own title bar. */

import { h, icon, ICONS, type Child, type ClassValue, type IconPath } from '../core/dom.ts';

export interface PanelOptions {
  /** The panel's name, in the header. */
  title: string;
  /** A count or a line beside the name, in the mono — "23 of 23". */
  meta?: string;
  /** Controls beside the name, on the left of the header. */
  lead?: Child[];
  /** Controls on the right of the header — usually `panelButton`s. */
  actions?: Child[];
  /** Extra classes on the panel itself, for its place in a layout. */
  class?: ClassValue;
  /** Tabs in the header in place of the name: one panel showing one of several bodies. */
  tabs?: readonly PanelTab[];
  /** Controls docked at the foot, under the body and not scrolled with it. */
  foot?: Child[];
  /** Folds to its header when its name is pressed; the key keeps it folded across renders. */
  collapsible?: string;
}

export interface PanelTab {
  label: string;
  /** A count beside the label, in the mono. */
  count?: string;
  on: boolean;
  onclick: () => void;
}

/** The collapsible panels folded this session, by key. */
const folded = new Set<string>();

function foldToggle(key: string, title: string, open: boolean): HTMLElement {
  return h(
    'button',
    {
      type: 'button',
      class: 'panel__fold',
      'aria-expanded': open ? 'true' : 'false',
      onclick: (e: Event) => {
        const btn = e.currentTarget as HTMLElement;
        const fold = !folded.delete(key);
        if (fold) folded.add(key);
        btn.closest('.panel')?.classList.toggle('panel--folded', fold);
        btn.setAttribute('aria-expanded', fold ? 'false' : 'true');
      },
    },
    icon(ICONS.chevronDown, { size: 12, width: 2, class: 'panel__chev' }),
    h('span', { class: 'panel__title', text: title }),
  );
}

/** A panel: header (title and actions) over a scrolling body. */
export function panel(opts: PanelOptions, ...body: Child[]): HTMLElement {
  const isFolded = opts.collapsible !== undefined && folded.has(opts.collapsible);
  return h(
    'section',
    {
      class: ['panel', isFolded && 'panel--folded', ...(Array.isArray(opts.class) ? opts.class : [opts.class])],
    },
    h(
      'header',
      { class: 'panel__head' },
      opts.tabs
        ? h(
            'div',
            { class: 'panel__tabs', role: 'tablist', 'aria-label': opts.title },
            ...opts.tabs.map((tab) =>
              h(
                'button',
                {
                  type: 'button',
                  role: 'tab',
                  class: ['panel__tab', tab.on && 'panel__tab--on'],
                  'aria-selected': tab.on ? 'true' : 'false',
                  onclick: tab.onclick,
                },
                h('span', { text: tab.label }),
                tab.count ? h('span', { class: 'panel__tab-count num', text: tab.count }) : null,
              ),
            ),
          )
        : opts.collapsible !== undefined
          ? foldToggle(opts.collapsible, opts.title, !isFolded)
          : h('span', { class: 'panel__title', text: opts.title }),
      opts.meta ? h('span', { class: 'panel__meta num', text: opts.meta }) : null,
      ...(opts.lead ?? []),
      h('span', { class: 'spacer' }),
      ...(opts.actions ?? []),
    ),
    h('div', { class: 'panel__body' }, ...body),
    opts.foot && opts.foot.length ? h('footer', { class: 'panel__foot' }, ...opts.foot) : null,
  );
}

/**
 * A button for a panel's header. Icon-only unless `text` is given; an icon-only
 * one must carry `label` for screen readers.
 */
export function panelButton(opts: {
  icon: IconPath;
  text?: string;
  title: string;
  label?: string;
  disabled?: boolean;
  onclick: (e?: Event) => void;
}): HTMLElement {
  return h(
    'button',
    {
      type: 'button',
      class: ['panel__btn', opts.text !== undefined && 'panel__btn--text'],
      disabled: opts.disabled || undefined,
      title: opts.title,
      'aria-label': opts.label,
      onclick: opts.onclick,
    },
    icon(opts.icon, { size: 13, width: 1.8 }),
    opts.text !== undefined ? h('span', { text: opts.text }) : null,
  );
}

/**
 * A panel unpinned: a rail at the edge with its name written down it, as Visual
 * Studio draws an auto-hidden tool window. Pressing it pins the panel back.
 */
export function panelRail(opts: {
  title: string;
  icon: IconPath;
  onclick: () => void;
}): HTMLElement {
  return h(
    'div',
    { class: 'panel-rail' },
    h(
      'button',
      {
        type: 'button',
        class: 'panel-rail__tab',
        title: `Show the ${opts.title.toLowerCase()} panel`,
        'aria-label': `Expand the ${opts.title.toLowerCase()} panel`,
        'aria-expanded': 'false',
        onclick: opts.onclick,
      },
      icon(opts.icon, { size: 13, width: 1.8 }),
      h('span', { class: 'panel-rail__label', text: opts.title }),
    ),
  );
}
